import { Box, Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  MultiProgressBarCard,
  PageTitleAndSubTitle,
  VerticalLabelValue,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import DeepDiveConstants from '../../../core-utils/messages/DeepDiveConstants';
import { numberToCurrencyFormatter } from '../../../core-utils/helper-utils/helper';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { CircularLoader } from '@HomaHealth/ui-component-library';
import { TopCardColorKeys } from '../../../pages/DeepDivePage';
import { getTextWithToolTip } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';
import Pagination from '@mui/material/Pagination';

export interface DeepDiveCategoryPopUpProps {
  open: boolean;
  setOpen: any;
  handleOpenSnackBar: any;
  selectedCategory: any;
  payload: any;
  handleTrendClick: any;
  handleItemClick: (
     
    label: any,
     
    clickedValue: any,
     
    isInternal?: boolean,
     
    selectedResultItem?: any,
  ) => void;
}

const DeepDiveCategoryPopUp: React.FC<DeepDiveCategoryPopUpProps> = observer(
  ({
    open,
    setOpen,
    selectedCategory,
    payload,
    handleOpenSnackBar,
    handleTrendClick,
    handleItemClick,
  }) => {
    const categoryColor =
      DeepDiveConstants.TOP_CARD_COLORS[
        selectedCategory?.identifier as TopCardColorKeys
      ]?.bg;
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '660px',
        height: '755px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      content: {
        display: 'flex',
        flexDirection: 'column',
        height: '615px',
        gap: '20px',
      },
      topCard: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        height: '100px',
        backgroundColor: categoryColor ?? '#99ACC2',
        borderRadius: '8px',
      },
      pagination: {
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: patientAccessTheme.palette.status.white,
        marginTop: '-13px',
        height: '26px',
      },
    });
    const { classes } = useStyles();

    //States
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [categoryData, setCategoryData] = useState<any>({});
    const [pageNumber, setPageNumber] = useState<number>(1);

    //Functions
    const handleClose = () => {
      setOpen(false);
    };

    const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
      setPageNumber(page);
    };

    //Apis
    const getCategoryData = async () => {
      setIsLoading(true);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.deepDiveBaseUrl +
            properties.categoryData,
          {
            ...payload,
            selectedCategory,
            pageNumber: pageNumber - 1,
            limit: 8,
          },
        )
        .then(async (res) => {
          if (res?.status === 200) {
            setCategoryData(res?.data?.data[selectedCategory?.value]);
            setIsLoading(false);
          }
        })
        .catch((ex) => {
          handleOpenSnackBar('Something went wrong', 'error', 3000);
          setIsLoading(false);
          return ex;
        });
    };

    useEffect(() => {
      getCategoryData();
    }, [pageNumber]);

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <PageTitleAndSubTitle
                title={getTextWithToolTip(selectedCategory.value, 65, {
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Inter',
                  color: patientAccessTheme.palette.text.highemphasis,
                })}
                subTitle={''}
                subTitleStyles={{ width: '0px' }}
              />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            {isLoading ? (
              <CircularLoader />
            ) : (
              <Grid className={classes.content}>
                <Grid className={classes.topCard}>
                  <VerticalLabelValue
                    title={DeepDiveConstants.PRIMARY_CATEGORY}
                    titleColor={patientAccessTheme.palette.grey[300]}
                    value={getTextWithToolTip(
                      selectedCategory?.value ?? '-',
                      30,
                      {
                        fontSize: '14px',
                        color: patientAccessTheme.palette.status.white,
                        fontWeight: 500,
                        fontFamily: 'Inter',
                        lineHeight: '43px',
                      },
                    )}
                  />
                  <VerticalLabelValue
                    title={DeepDiveConstants.AMOUNT}
                    titleColor={patientAccessTheme.palette.grey[300]}
                    value={
                      <Typography
                        fontSize="14px"
                        lineHeight="43px"
                        textColor={patientAccessTheme.palette.status.white}
                        fontType="semiBold"
                      >
                        {numberToCurrencyFormatter(
                          categoryData?.totalPaidAmount ?? 0,
                          true,
                          2,
                        )}
                      </Typography>
                    }
                  />
                  <VerticalLabelValue
                    title={DeepDiveConstants.TOTAL_PERCENT}
                    titleColor={patientAccessTheme.palette.grey[300]}
                    value={
                      <Typography
                        fontSize="14px"
                        lineHeight="43px"
                        textColor={patientAccessTheme.palette.status.white}
                        fontType="semiBold"
                      >
                        {`${categoryData?.totalPercent?.toFixed(2) ?? '-'}%`}
                      </Typography>
                    }
                  />
                </Grid>
                <MultiProgressBarCard
                  mainLabel={getTextWithToolTip(selectedCategory.value, 30, {
                    fontSize: '12px',
                    color: patientAccessTheme.palette.text.highemphasis,
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    width: '40%',
                  })}
                  showTrendButton={true}
                  data={categoryData?.result?.map((item: any) => {
                    return {
                      name: item.value,
                      percent: item?.percent?.toFixed(2) ?? 0,
                      categoryPercent: `${
                        item?.categoryPercent?.toFixed(2) ?? '0'
                      }%`,
                      cost: numberToCurrencyFormatter(
                        item?.total_paid_amount ?? 0,
                        true,
                        2,
                      ),
                      handleOnClick: () => {
                        setOpen(false);
                        handleItemClick(
                          selectedCategory?.value,
                          item?.value,
                          true,
                          item,
                        );
                      },
                      handleTrendOnClick: () =>
                        handleTrendClick(selectedCategory?.identifier, item),
                    };
                  })}
                  visibleLength={30}
                  headerStyles={{ backgroundColor: '#EFF2F6' }}
                  bodyStyles={{
                    backgroundColor: '#FFFFFF',
                    height: 'fit-content',
                    gap: '18px',
                    padding: '16px 8px 16px 16px',
                  }}
                  barColor={categoryColor ?? '#99ACC2'}
                  barWidth="572px"
                  barBackground="#2E2F331A"
                  valueStyles={{ fontSize: '14px' }}
                  mainGridStyles={{
                    width: '602px',
                    height: 'fit-content',
                    border: '0px',
                  }}
                  isTableLike={true}
                  titleRightMostItem={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '29px',
                        marginRight: '25px',
                        width: '300px',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        fontSize="12px"
                        lineHeight="12px"
                        fontType="medium"
                        fontFamily="Inter"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        % of Total
                      </Typography>
                      <Typography
                        fontSize="12px"
                        lineHeight="12px"
                        fontType="medium"
                        fontFamily="Inter"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        {getTextWithToolTip(
                          `% of ${selectedCategory.value}`,
                          12,
                          {
                            textColor:
                              patientAccessTheme.palette.text.highemphasis,
                          },
                        )}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        lineHeight="12px"
                        fontType="medium"
                        fontFamily="Inter"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        Amount
                      </Typography>
                    </div>
                  }
                />
                <Box className={classes.pagination}>
                  <Pagination
                    count={categoryData?.totalPages}
                    page={pageNumber}
                    boundaryCount={1}
                    color="primary"
                    size="small"
                    shape="circular"
                    onChange={handlePageChange}
                  />
                </Box>
              </Grid>
            )}

            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 0px 20px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary.main}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {DeepDiveConstants.CLOSE}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  handleClose();
                }}
              />
            </Grid>
          </Paper>
        </Modal>
      </>
    );
  },
);

export default DeepDiveCategoryPopUp;
